import log from '@/lib/logger';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Member, MembershipStatus, MemberUpdateMembershipRequest} from "@/models/member.ts";
import ValidationAlert from "@/components/validation-alert.tsx";
import {Field, Label} from "@/components/tailwind/fieldset.tsx";
import {updateMemberMembership} from "@/services/member-services.ts";
import {MembershipFeeType} from "@/models/membership-fee.ts";
import {assignMembershipFeeType} from "@/services/membership-fee-services.ts";
import {useAuth} from "@/context/use-auth.tsx";
import {useCallback, useEffect, useMemo} from "react";
import {Input} from "@/components/tailwind/input.tsx";
import {Select} from "@/components/tailwind/select.tsx";

interface MemberEditMembershipDialogProps {
    isOpen: boolean;
    onSave: (data: Member) => void;
    onClose: () => void;
    memberData: Member;
    availableStatus: MembershipStatus[];
    membershipFeeTypes?: MembershipFeeType[];
    currentFeeTypeId?: string;
}

function isValidDate(dateStr: string): boolean {
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
}


const memberEditMembershipSchema = z.object({
    membershipStartDate: z.string()
        .refine((value) => Boolean(value) && isValidDate(value),
            {
                message: "memberEditDialog.membership.errors.invalid-membership-start-date-format"
            }
        )
        .refine(
            (value) => value !== undefined && value !== null && value !== "",
            {
                message: "memberEditDialog.membership.errors.membership-start-required"
            }
        ),

    membershipStatus: z.object({
        id: z.string()
            .uuid({
                message: "memberEditDialog.membership.errors.invalid-membership-status"
            })
            .min(1, {
                message: "memberEditDialog.membership.errors.membership-status-required"
            }),
        value: z.string()
            .min(1, {
                message: "memberEditDialog.membership.errors.membership-status-required"
            })
    }),
    
    membershipFeeTypeId: z.string().optional()
}) satisfies z.ZodType<{
    membershipStartDate: string;
    membershipStatus: MembershipStatus;
    membershipFeeTypeId?: string;
}>;


export type MemberEditMembershipFormData = z.infer<typeof memberEditMembershipSchema>;

export default function MemberEditMembershipDialog({
                                                       isOpen,
                                                       onSave,
                                                       onClose,
                                                       memberData,
                                                       availableStatus,
                                                       membershipFeeTypes,
                                                       currentFeeTypeId,
                                                   }: MemberEditMembershipDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    
    // Map the memberStatus from backend to the correct status object
    const getMappedStatus = useCallback(() => {
        if (!memberData.memberStatus || !availableStatus) return availableStatus?.[0];
        
        // Map ACTIVE_MEMBER/PASSIVE_MEMBER to the localized status object
        const statusMap: Record<string, string> = {
            'ACTIVE_MEMBER': 'Aktiv',
            'PASSIVE_MEMBER': 'Passiv'
        };
        
        const targetValue = statusMap[memberData.memberStatus];
        return availableStatus.find(s => s.value === targetValue) || availableStatus[0];
    }, [memberData.memberStatus, availableStatus]);
    
    const defaultValues = useMemo(() => ({
        membershipStartDate: memberData.membershipStartDate,
        membershipStatus: getMappedStatus(),
        membershipFeeTypeId: currentFeeTypeId || membershipFeeTypes?.find(t => t.isDefault)?.id,
    }), [memberData.membershipStartDate, currentFeeTypeId, membershipFeeTypes, getMappedStatus]);

    const {
        handleSubmit,
        setError,
        control,
        formState: {errors},
        reset
    } = useForm<MemberEditMembershipFormData>({
        resolver: zodResolver(memberEditMembershipSchema),
        defaultValues
    });

    useEffect(() => {
        if (isOpen && memberData) {
            reset(defaultValues);
        }
    }, [isOpen, memberData, reset]);

    const {token} = useAuth();

    async function saveData(data: MemberEditMembershipFormData): Promise<boolean> {

        const membershipStartDate = new Date(data.membershipStartDate);
        if (memberData.dateOfBirth) {
            const dateOfBirth = new Date(memberData.dateOfBirth)
            if (membershipStartDate < dateOfBirth) {
                setError("membershipStartDate", {
                    type: "manual",
                    message: "memberEditDialog.membership.errors.start-date-before-birthday"
                })

                return false;
            }
        }

        // Map the localized status to the enum value
        const statusMapping: Record<string, string> = {
            'Aktiv': 'ACTIVE_MEMBER',
            'Passiv': 'PASSIVE_MEMBER',
            'ACTIVE_MEMBER': 'ACTIVE_MEMBER',
            'PASSIVE_MEMBER': 'PASSIVE_MEMBER'
        };
        
        const membershipData: MemberUpdateMembershipRequest = {
            memberId: memberData.id,
            membershipStartDate: data.membershipStartDate,
            membershipStatusId: data.membershipStatus.id, // kept for potential frontend compatibility
            memberStatus: (statusMapping[data.membershipStatus.value] || data.membershipStatus.value) as any
        }

        try {
            const response = await updateMemberMembership(token, membershipData);
            
            // Also update fee type if changed
            if (data.membershipFeeTypeId && data.membershipFeeTypeId !== currentFeeTypeId) {
                await assignMembershipFeeType(token, {
                    memberId: memberData.id,
                    feeTypeId: data.membershipFeeTypeId,
                    validFrom: new Date().toISOString().split('T')[0]
                });
            }
            
            if (response) {
                onSave(response)
            }
            return true
        } catch (error: any) {
            log.error("Error updating member: ", error);
            if (error.status) {
                setError("root", {
                    message: tCommon("serverError.response-code-with-details", {
                        statusCode: error.status,
                        errorDetails: error.error
                    })
                });
            } else {
                setError("root", {message: tCommon("serverError.unknown-with-details", {errorDetails: error.message})});
            }

            return false;
        }

    }

    async function onSubmit(data: MemberEditMembershipFormData) {
        if (await saveData(data)) {
            handleClosing();
        }
    }

    function handleClosing() {
        reset();
        onClose();
    }

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>
                {t('memberEditDialog.membership.title')}
            </DialogTitle>

            <DialogBody>
                {errors.root && (
                    <ValidationAlert message={errors.root?.message ||
                        tCommon("serverError.unknown")}/>
                )}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <Field>
                            <Label>{t('memberEditDialog.membership.fields.membershipStart.label')}</Label>
                            <Controller
                                name="membershipStartDate"
                                control={control}
                                render={({field}) => (
                                    <Input
                                        type="date"
                                        value={field.value || ''}
                                        max={new Date().toISOString().split('T')[0]}
                                        onChange={(e) => {
                                            const value = e.target.value || null;
                                            field.onChange(value);
                                        }}
                                        aria-label={t('memberEditDialog.membership.fields.membershipStart.ariaLabel')}
                                        invalid={Boolean(errors.membershipStartDate)}
                                    />
                                )}
                            />
                        </Field>
                        {errors.membershipStartDate && (
                            <ValidationAlert
                                message={t(errors.membershipStartDate?.message ||
                                    "members.memberEditDialog.membership.errors.unknown-membership-start-date-issue")}
                            />
                        )}
                    </div>

                    <div>
                        <Field>
                            <Label>{t('memberEditDialog.membership.fields.status.label')}</Label>
                            <Controller
                                name="membershipStatus"
                                control={control}
                                rules={{required: true}}
                                render={({field}) => (
                                    <Select
                                        id="membership-status"
                                        value={field.value?.id}
                                        onChange={e => {
                                            const id = e.target.value;
                                            field.onChange(availableStatus?.find(s => s.id === id));
                                        }}
                                        aria-label={t('memberEditDialog.membership.fields.status.ariaLabel')}
                                        invalid={Boolean(errors.membershipStatus)}
                                        data-testid="member-status-select"
                                    >
                                        {availableStatus?.map(item => (
                                            <option key={item.id} value={item.id}>
                                                {item.value}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                            />

                        </Field>
                        {errors.membershipStatus && (
                            <ValidationAlert
                                message={t((errors.membershipStatus?.message ||
                                    "memberEditDialog.membership.errors.unknown-membership-status-issue") as any)}
                            />
                        )}
                    </div>

                    {membershipFeeTypes && membershipFeeTypes.length > 0 && (
                        <div>
                            <Field>
                                <Label>{t('memberEditDialog.membership.fields.membershipFeeType.label')}</Label>
                                <Controller
                                    name="membershipFeeTypeId"
                                    control={control}
                                    render={({field}) => (
                                        <Select
                                            id="membership-fee-type"
                                            value={field.value || ''}
                                            onChange={field.onChange}
                                            aria-label={t('memberEditDialog.membership.fields.membershipFeeType.ariaLabel')}
                                        >
                                            {membershipFeeTypes.map(feeType => (
                                                <option key={feeType.id} value={feeType.id}>
                                                    {feeType.name} {feeType.currentAmount !== undefined && (
                                                        feeType.currentAmount === 0 ? '(beitragsfrei)' : `(${feeType.currentAmount.toFixed(2)} €/Jahr)`
                                                    )}
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </Field>
                        </div>
                    )}

                    <DialogActions>
                        <Button
                            type="button"
                            plain
                            onClick={handleClosing}
                            aria-label={tCommon('buttons.cancel.ariaLabel')}
                        >
                            {tCommon('buttons.cancel.text')}
                        </Button>
                        <Button
                            type="submit"
                            data-testid="save-membership-button"
                            aria-label={tCommon('buttons.submit.ariaLabel')}
                        >
                            {tCommon('buttons.submit.text')}
                        </Button>
                    </DialogActions>
                </form>
            </DialogBody>
        </Dialog>
    );
}
